/**
 * 大分類業種のインターフェース
 */
export interface DisplayIndustryL {
  /** 大分類業種ID */
  id: string;
  /** 大分類業種名 */
  name: string;
  /** 小分類業種リスト */
  industrySList: IndustryS[];
  /** 求人数 */
  jobCount: number;
}

/**
 * 小分類業種のインターフェース
 */
export interface IndustryS {
  /** 小分類業種ID */
  id: string;
  /** 小分類業種名 */
  name: string;
  /** 求人数 */
  jobCount: number;
}

/**
 * チェック状態のインターフェース
 */
export interface CheckState {
  /** 選択されている大分類業種IDリスト */
  L: string[];
  /** 選択されている小分類業種IDリスト */
  S: string[];
  /** 表示される業種リスト */
  industry: DisplayIndustryL[];
}

/**
 * Reducerアクションのタイプ
 */
type ReducerAction =
  | {
      /** リセットアクション */
      type: 'reset';
    }
  | {
      /** 大分類業種の変更アクション */
      type: 'changeL';
      value: {
        /** 大分類業種ID */
        id: string;
      };
    }
  | {
      /** 小分類業種の変更アクション */
      type: 'changeS';
      value: {
        /** 小分類業種ID */
        id: string;
        /** 対応する大分類業種ID */
        idL: string;
      };
    }
  | {
      /** 状態の更新アクション */
      type: 'update';
      payload: Partial<CheckState>;
    };

/**
 * 業種チェック状態を管理するためのリデューサー関数
 *
 * @param {CheckState} state - 現在のチェック状態
 * @param {ReducerAction} action - リデューサーアクション
 * @returns {CheckState} - 更新されたチェック状態
 */
export const industryCheckReducer = (
  state: CheckState,
  action: ReducerAction
) => {
  const add = (id: string[], type: 'L' | 'S') => [
    ...state[type],
    ...id.filter((i) => !state[type].includes(i)),
  ];

  const remove = (id: string[], type: 'L' | 'S') =>
    state[type].filter((stateId) => !id.includes(stateId));

  const getIndustryL = (id: string) =>
    state.industry.find((industryL) => industryL.id === id);

  switch (action.type) {
    case 'changeL': {
      const { id } = action.value;
      const checked = state.L.includes(id);

      const newL = checked ? remove([id], 'L') : add([id], 'L');

      const idSList = getIndustryL(id)?.industrySList.map((indS) => indS.id);

      let newS: string[] = [...state.S];
      if (checked) {
        if (idSList) newS = remove(idSList, 'S');
      } else if (idSList) newS = add(idSList, 'S');
      return {
        ...state,
        L: newL,
        S: newS,
      };
    }
    case 'changeS': {
      const { id, idL } = action.value;

      const checked = state.S.includes(id);
      const newS = checked ? remove([id], 'S') : add([id], 'S');

      let newL: string[] = [...state.L];

      const siblingIdList = getIndustryL(idL)
        ?.industrySList.map((ind) => ind.id)
        .filter((idS) => idS !== id);

      if (
        siblingIdList &&
        siblingIdList.every((otherId) => state.S.includes(otherId))
      ) {
        // 同じ大種別の小分類職種が選択済みの時
        newL = checked ? remove([idL], 'L') : add([idL], 'L');
      }

      return {
        ...state,
        S: newS,
        L: newL,
      };
    }
    case 'update':
      return {
        ...state,
        ...action.payload,
      };
    case 'reset': {
      return { ...state, L: [], S: [] };
    }
    default:
      throw new Error();
  }
};
